import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  IconButton
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSignUp, useClerk, useAuth, useSignIn } from '@clerk/clerk-react';
import Header from '../common/Header';
import Footer from '../common/Footer';
import { validateInvitation, registerInvitedUser } from '../../services/adminService';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { API_URL } from '../../config';

// Define steps enum for better type safety
enum SignupStep {
  VERIFY_INVITATION = 0,
  CREATE_ACCOUNT = 1,
  CONFIRMATION = 2
}

const InvitationSignup: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signUp, isLoaded: isSignUpLoaded } = useSignUp();
  const { signIn, isLoaded: isSignInLoaded } = useSignIn();
  const { signOut, setActive } = useClerk();
  const { isSignedIn, userId } = useAuth();

  // Extract invitation token from URL
  const searchParams = new URLSearchParams(location.search);
  const invitationToken = searchParams.get('token');

  // Form state
  const [activeStep, setActiveStep] = useState<SignupStep>(SignupStep.VERIFY_INVITATION);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [validatingInvitation, setValidatingInvitation] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invitationValid, setInvitationValid] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Invitation data
  const [invitationEmail, setInvitationEmail] = useState('');
  const [invitationFirstName, setInvitationFirstName] = useState('');
  const [invitationLastName, setInvitationLastName] = useState('');

  const steps = ['Verify Invitation', 'Create Account', 'Confirmation'];

  const [verificationStep, setVerificationStep] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [clerkUserId, setClerkUserId] = useState<string | null>(null);

  // Special state for handling existing Clerk users
  const [existingClerkUser, setExistingClerkUser] = useState(false);
  const [existingUserInfo, setExistingUserInfo] = useState<{email: string}>({email: ''});

  // Validate invitation token when component mounts
  useEffect(() => {
    const verifyInvitation = async () => {
      if (!invitationToken) {
        setError('No invitation token provided');
        setValidatingInvitation(false);
        return;
      }

      try {
        setValidatingInvitation(true);
        const invitationData = await validateInvitation(invitationToken);
        
        if (invitationData.valid) {
          setInvitationValid(true);
          setInvitationEmail(invitationData.email);
          setEmail(invitationData.email);
          
          if (invitationData.firstName) {
            setInvitationFirstName(invitationData.firstName);
            setFirstName(invitationData.firstName);
          }
          
          if (invitationData.lastName) {
            setInvitationLastName(invitationData.lastName);
            setLastName(invitationData.lastName);
          }
        } else {
          setError('This invitation is no longer valid');
        }
      } catch (error: any) {
        setError(error.message || 'Invalid invitation link');
      } finally {
        setValidatingInvitation(false);
        setLoading(false);
      }
    };

    verifyInvitation();
  }, [invitationToken]);

  // Add a check for already signed in users
  useEffect(() => {
    const handleExistingClerkUser = async () => {
      // If the user is already signed in and the invitation is valid
      if (isSignedIn && userId && invitationValid && invitationToken) {
        setLoading(true);
        setError(null);
        try {
          // Accept the invitation using the existing Clerk user ID
          const response = await fetch(`${API_URL}/invitations/accept`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: invitationToken, clerkUserId: userId })
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            setError(errorData.error || 'Failed to accept invitation. Please contact support.');
            setLoading(false);
            return;
          }
          
          // Redirect to dashboard after successful acceptance
          navigate('/dashboard');
        } catch (error: any) {
          setError(error.message || 'Error accepting invitation. Please try again.');
          setLoading(false);
        }
      }
    };

    if (invitationValid && !validatingInvitation) {
      handleExistingClerkUser();
    }
  }, [isSignedIn, userId, invitationValid, invitationToken, navigate, validatingInvitation]);

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    let isValid = true;

    if (!firstName.trim()) {
      errors.firstName = 'First name is required';
      isValid = false;
    }

    if (!lastName.trim()) {
      errors.lastName = 'Last name is required';
      isValid = false;
    }

    if (!password) {
      errors.password = 'Password is required';
      isValid = false;
    } else if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
      isValid = false;
    }

    if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  // Handle next step for existing Clerk user login
  const handleExistingUserLogin = async () => {
    if (!password) {
      setFormErrors({ password: 'Password is required' });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (!signIn) {
        setError('System error: Unable to initialize the sign-in process.');
        setLoading(false);
        return;
      }
      
      // Try to sign in with the provided credentials
      const signInAttempt = await signIn.create({
        identifier: existingUserInfo.email || email,
        password,
      });
      
      if (signInAttempt.status === 'complete' && signInAttempt.createdSessionId) {
        // Set the active session
        await setActive({
          session: signInAttempt.createdSessionId
        });
        
        // Accept the invitation using the user's Clerk ID
        const currentUserId = userId || '';
        
        try {
          const response = await fetch(`${API_URL}/invitations/accept`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: invitationToken, clerkUserId: currentUserId })
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            setError(errorData.error || 'Failed to accept invitation. Please contact support.');
            setLoading(false);
            return;
          }
          
          // Redirect to dashboard
          navigate('/dashboard');
        } catch (error: any) {
          setError(error.message || 'Error accepting invitation. Please try again.');
          setLoading(false);
          return;
        }
      } else if (signInAttempt.status === 'needs_second_factor') {
        // Handle 2FA if needed
        setError('Two-factor authentication required. Please use the same email and password you originally registered with.');
        setLoading(false);
        return;
      } else {
        // Failed to sign in
        setError('Incorrect password. Please use the password you originally registered with.');
        setLoading(false);
        return;
      }
    } catch (signInError: unknown) {
      // Failed to sign in
      console.error('Sign in error:', signInError);
      const errorMsg = signInError instanceof Error ? signInError.message : String(signInError);
      
      // Provide more helpful error message
      if (errorMsg.includes('incorrect password') || errorMsg.includes('failed to authenticate')) {
        setError('Incorrect password. Please use the password you originally registered with.');
      } else {
        setError('Authentication failed. Please check your credentials and try again.');
      }
      
      setLoading(false);
      return;
    }
  };

  const handleNext = async () => {
    if (activeStep === SignupStep.VERIFY_INVITATION) {
      // If the user is already signed in, check if they want to use their existing account
      if (isSignedIn && userId) {
        setActiveStep(SignupStep.CONFIRMATION); // Skip to confirmation step
        return;
      }
      setActiveStep(SignupStep.CREATE_ACCOUNT);
      return;
    }
    
    if (activeStep === SignupStep.CREATE_ACCOUNT && !verificationStep) {
      // Registration form submit
      if (!validateForm()) return;
      setLoading(true);
      setError(null);
      
      try {
        if (!signUp) throw new Error('Clerk signUp not loaded');
        
        // Attempt to create the user in Clerk
        const result = await signUp.create({
          emailAddress: email,
          password,
          firstName,
          lastName
        });
        
        console.log('Clerk signUp.create result:', result);
        
        // Prepare email verification (send confirmation email)
        const prepResult = await signUp.prepareEmailAddressVerification();
        console.log('Clerk signUp.prepareEmailAddressVerification result:', prepResult);
        setVerificationStep(true);
        setLoading(false);
      } catch (err: any) {
        console.error('Clerk signUp.create error:', err);
        const errorMsg = err.errors?.[0]?.message || err.message || 'Error creating account';
        
        // Check if this is an "email taken" error
        if (errorMsg.includes('email address is taken') || errorMsg.includes('identifier already exists')) {
          console.log('Email already exists in Clerk. Attempting to sign in with provided password...');
          
          try {
            if (!signIn) {
              setError('System error: Unable to initialize the sign-in process.');
              setLoading(false);
              return;
            }
            
            // First try to sign in with the email and password the user already provided
            const signInAttempt = await signIn.create({
              identifier: email,
              password,
            });
            
            // If sign-in is successful with the provided password
            if (signInAttempt.status === 'complete' && signInAttempt.createdSessionId) {
              // Set the active session
              await setActive({
                session: signInAttempt.createdSessionId
              });
              
              // Accept the invitation using the user's Clerk ID
              const currentUserId = userId || '';
              
              try {
                const response = await fetch(`${API_URL}/invitations/accept`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ token: invitationToken, clerkUserId: currentUserId })
                });
                
                if (!response.ok) {
                  const errorData = await response.json();
                  setError(errorData.error || 'Failed to accept invitation. Please contact support.');
                  setLoading(false);
                  return;
                }
                
                // Redirect to dashboard
                navigate('/dashboard');
                return;
              } catch (error: any) {
                setError(error.message || 'Error accepting invitation. Please try again.');
                setLoading(false);
                return;
              }
            } else if (signInAttempt.status === 'needs_second_factor') {
              // Handle 2FA
              setError('Two-factor authentication required. Please use the same email and password you originally registered with.');
              // Show the password entry UI
              setExistingClerkUser(true);
              setExistingUserInfo({email});
              setError(null);
              setLoading(false);
              // Clear password fields for security
              setPassword('');
              setConfirmPassword('');
              return;
            } else {
              // Initial sign-in attempt failed, switch to password entry UI
              console.log('Initial sign-in attempt failed. Showing password entry form.');
              setExistingClerkUser(true);
              setExistingUserInfo({email});
              setError(null);
              setLoading(false);
              // Clear password fields for security
              setPassword('');
              setConfirmPassword('');
              return;
            }
          } catch (signInError: unknown) {
            // Sign-in attempt failed, show password entry UI
            console.error('Sign-in error:', signInError);
            setExistingClerkUser(true);
            setExistingUserInfo({email});
            setError(null);
            setLoading(false);
            // Clear password fields for security
            setPassword('');
            setConfirmPassword('');
            return;
          }
        } else {
          // Other Clerk error
          setError(errorMsg);
          setLoading(false);
        }
      }
      return;
    }
    
    if (activeStep === SignupStep.CREATE_ACCOUNT && verificationStep) {
      // Verification code submit
      if (!verificationCode) {
        setFormErrors({ verificationCode: 'Verification code is required' });
        return;
      }
      setLoading(true);
      setError(null);
      try {
        if (!signUp) throw new Error('Clerk signUp not loaded');
        const result = await signUp.attemptEmailAddressVerification({ code: verificationCode });
        console.log('Clerk signUp.attemptEmailAddressVerification result:', result);
        if (result.status === 'complete') {
          setClerkUserId(result.createdUserId);
          // Call backend to accept invitation and add to org
          try {
            const response = await fetch(`${API_URL}/invitations/accept`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token: invitationToken, clerkUserId: result.createdUserId })
            });
            console.log('Backend /invitations/accept response:', response);
            if (!response.ok) {
              const errorData = await response.json();
              console.error('Backend /invitations/accept error:', errorData);
              // Do not sign out, just show the error
              setError(errorData.error || 'Failed to complete registration. Please contact support.');
              setLoading(false);
              return;
            }
            
            setActiveStep(SignupStep.CONFIRMATION);
            
            // Use the createdSessionId directly to set the active session
            if (result.createdSessionId) {
              try {
                // Sign in the user automatically
                await setActive({
                  session: result.createdSessionId
                });
                
                // Now that we're authenticated, navigate to dashboard
                navigate('/dashboard');
              } catch (sessionErr: any) {
                console.error('Error setting active session:', sessionErr);
                // If session activation fails, redirect to login page with a message
                setError('Account created successfully. Please sign in to continue.');
                setTimeout(() => {
                  navigate('/login');
                }, 2000);
              }
            } else {
              // No session ID available, redirect to login
              setError('Account created successfully. Please sign in to continue.');
              setTimeout(() => {
                navigate('/login');
              }, 2000);
            }
            
            setLoading(false);
          } catch (backendErr: any) {
            setError(backendErr.message || 'Failed to complete registration. Please contact support.');
            setLoading(false);
          }
        } else {
          setError('Verification failed. Please check your code.');
          setLoading(false);
        }
      } catch (err: any) {
        setError(err.errors?.[0]?.message || err.message || 'Verification error');
        console.error('Clerk signUp.attemptEmailAddressVerification error:', err);
        setLoading(false);
      }
      return;
    }
    
    if (activeStep === SignupStep.CONFIRMATION) {
      // If user is already signed in, accept the invitation with their existing Clerk ID
      if (isSignedIn && userId) {
        setLoading(true);
        setError(null);
        try {
          const response = await fetch(`${API_URL}/invitations/accept`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: invitationToken, clerkUserId: userId })
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            setError(errorData.error || 'Failed to accept invitation. Please contact support.');
            setLoading(false);
            return;
          }
          
          // Redirect to dashboard
          navigate('/dashboard');
        } catch (error: any) {
          setError(error.message || 'Error accepting invitation. Please try again.');
          setLoading(false);
        }
        return;
      }
      
      // If we got here through normal verification flow, do nothing
      return;
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev === SignupStep.VERIFY_INVITATION ? 
      SignupStep.VERIFY_INVITATION : prev === SignupStep.CREATE_ACCOUNT ? 
      SignupStep.VERIFY_INVITATION : SignupStep.CREATE_ACCOUNT);
  };

  if (!isSignUpLoaded || validatingInvitation) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!invitationValid && !validatingInvitation) {
    return (
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#f4f6f8"
        }}
      >
        <Header />
        <Container maxWidth="sm" sx={{ py: 6, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Paper elevation={3} sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="error" gutterBottom>
              Invalid Invitation
            </Typography>
            <Typography variant="body1" paragraph>
              The invitation link you used is invalid or has expired.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Please contact your administrator to request a new invitation.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/login')}
              sx={{
                mt: 2,
                backgroundColor: "#5D5FEF",
                color: "#fff",
                '&:hover': {
                  backgroundColor: "#4A4BD5",
                }
              }}
            >
              Back to Login
            </Button>
          </Paper>
        </Container>
        <Footer />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f4f6f8"
      }}
    >
      <Header />

      <Container maxWidth="md" sx={{ py: 6, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Paper elevation={3} sx={{ p: { xs: 3, md: 4 }, borderRadius: 2 }}>
          <Typography variant="h4" align="center" gutterBottom>
            Complete Your Registration
          </Typography>
          
          <Stepper activeStep={Number(activeStep)} alternativeLabel sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {existingClerkUser && (
            <Box sx={{ mb: 4 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                This email is already registered in our system. Please enter your existing password to continue.
              </Alert>
              
              <Typography variant="h6" gutterBottom>
                Sign in with Existing Account
              </Typography>
              
              <TextField
                fullWidth
                label="Email Address"
                value={existingUserInfo.email || email}
                disabled
                margin="normal"
              />
              
              <TextField
                fullWidth
                label="Your Existing Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={!!formErrors.password}
                helperText={formErrors.password}
                margin="normal"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleTogglePassword} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Button
                  onClick={() => {
                    setExistingClerkUser(false);
                    setError(null);
                  }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleExistingUserLogin}
                  disabled={loading}
                  sx={{
                    backgroundColor: "#5D5FEF",
                    color: "#fff",
                    '&:hover': {
                      backgroundColor: "#4A4BD5",
                    }
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Continue'
                  )}
                </Button>
              </Box>
            </Box>
          )}

          {!existingClerkUser && (
            <>
              {activeStep === SignupStep.VERIFY_INVITATION && (
                <Box>
                  <Alert severity="success" sx={{ mb: 3 }}>
                    Your invitation is valid! You've been invited to join the platform.
                  </Alert>
                  
                  <Typography variant="body1" paragraph>
                    You are registering with the following email address:
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label="Email Address"
                    value={invitationEmail}
                    disabled
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Click "Next" to set up your account information.
                  </Typography>
                </Box>
              )}

              {activeStep === SignupStep.CREATE_ACCOUNT && !verificationStep && (
                <Box>
                  {isSignedIn ? (
                    <Alert severity="info" sx={{ mb: 3 }}>
                      You are already signed in. Click "Next" to use your existing account.
                    </Alert>
                  ) : (
                    <>
                      <Typography variant="h6" gutterBottom>
                        Your Account Details
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                          fullWidth
                          label="First Name"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          error={!!formErrors.firstName}
                          helperText={formErrors.firstName}
                          margin="normal"
                          required
                        />
                        <TextField
                          fullWidth
                          label="Last Name"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          error={!!formErrors.lastName}
                          helperText={formErrors.lastName}
                          margin="normal"
                          required
                        />
                      </Box>
                      <TextField
                        fullWidth
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        error={!!formErrors.password}
                        helperText={formErrors.password}
                        margin="normal"
                        required
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={handleTogglePassword} edge="end">
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        fullWidth
                        label="Confirm Password"
                        type={showPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        error={!!formErrors.confirmPassword}
                        helperText={formErrors.confirmPassword}
                        margin="normal"
                        required
                      />
                    </>
                  )}
                </Box>
              )}

              {activeStep === SignupStep.CREATE_ACCOUNT && verificationStep && (
                <Box>
                  <Typography>Enter the verification code sent to your email:</Typography>
                  <TextField
                    value={verificationCode}
                    onChange={e => setVerificationCode(e.target.value)}
                    label="Verification Code"
                    sx={{ mt: 2 }}
                    error={!!formErrors.verificationCode}
                    helperText={formErrors.verificationCode}
                  />
                </Box>
              )}

              {activeStep === SignupStep.CONFIRMATION && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Confirm Your Details
                  </Typography>
                  
                  <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1, mb: 3 }}>
                    <Typography variant="subtitle2">Account</Typography>
                    {isSignedIn ? (
                      <Alert severity="success" sx={{ mt: 1, mb: 1 }}>
                        You'll join the organization with your existing Clerk account.
                      </Alert>
                    ) : (
                      <>
                        <Typography variant="body2">{firstName} {lastName}</Typography>
                        <Typography variant="body2">{email}</Typography>
                      </>
                    )}
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary">
                    By clicking "Complete Registration", you agree to our Terms of Service and Privacy Policy.
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                  disabled={activeStep === SignupStep.VERIFY_INVITATION || loading}
                  onClick={handleBack}
                  sx={{ mr: 1 }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                  disabled={loading || (activeStep === SignupStep.CREATE_ACCOUNT && verificationStep && !verificationCode)}
                  sx={{
                    backgroundColor: "#5D5FEF",
                    color: "#fff",
                    py: 1.5,
                    px: 3,
                    '&:hover': {
                      backgroundColor: "#4A4BD5",
                    }
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : activeStep === SignupStep.CONFIRMATION ? (
                    'Complete Registration'
                  ) : (
                    'Next'
                  )}
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Container>
      
      <Footer />
    </Box>
  );
};

export default InvitationSignup; 